import { Building2, GraduationCap, History, Trophy, Users } from "lucide-react";
import { useState } from "react";
import {
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useSubjectPerformance } from "@/hooks/queries/use-subject-performance";
import { useSubjectPerformanceChart } from "@/hooks/queries/use-subject-performance-chart";
import type {
	CategoryPerformance,
	SubjectPerformanceChartItem,
	SubjectRankEntry,
} from "@/types/subject-performance";

interface SubjectPerformanceModalProps {
	applicationId: string;
	subjectId: string;
	subjectName: string;
}

/** Converte percentuais formatados pelo backend ("86.67%", "-%") em número. */
function parsePercent(value: string | number): number {
	const parsed = Number.parseFloat(String(value));
	return Number.isFinite(parsed) ? Math.min(Math.max(parsed, 0), 100) : 0;
}

function RankCard({
	icon: Icon,
	label,
	rank,
}: {
	icon: React.ComponentType<{ size?: number; className?: string }>;
	label: string;
	rank: SubjectRankEntry;
}) {
	return (
		<div className="flex items-center gap-4 p-5 bg-white border border-slate-100 rounded-xl shadow-sm">
			<div className="size-10 shrink-0 rounded-full bg-primary/10 flex items-center justify-center">
				<Icon size={20} className="text-primary" />
			</div>
			<div className="flex flex-col min-w-0">
				<span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
					{label}
				</span>
				<span className="font-bold text-slate-900 tabular-nums">
					{rank.value}º{" "}
					<span className="text-sm font-semibold text-slate-400">
						de {rank.total}
					</span>
				</span>
			</div>
		</div>
	);
}

function CategorySection({
	title,
	items,
	emptyMessage,
}: {
	title: string;
	items: CategoryPerformance[];
	emptyMessage: string;
}) {
	return (
		<div className="space-y-3">
			<h3 className="text-sm font-bold text-slate-900">{title}</h3>
			{items.length === 0 ? (
				<div className="py-6 text-center text-sm text-slate-400 bg-slate-50 rounded-md">
					{emptyMessage}
				</div>
			) : (
				<div className="space-y-2">
					{items.map((item) => {
						const percent = parsePercent(item.performance);
						return (
							<div
								key={item.name}
								className="p-4 bg-white border border-slate-100 rounded-xl space-y-2"
							>
								<div className="flex flex-wrap justify-between items-baseline gap-x-4 gap-y-1">
									<span className="text-sm font-medium text-slate-700">
										{item.name}
										<span className="text-xs text-slate-400 font-normal">
											{" "}
											· {item.count} {item.count === 1 ? "questão" : "questões"}
										</span>
									</span>
									<span className="text-sm font-bold tabular-nums text-slate-900">
										{percent.toFixed(0)}%
									</span>
								</div>
								<div className="h-1.5 w-full bg-primary/10 rounded-full overflow-hidden">
									<div
										className="h-full rounded-full bg-primary transition-all"
										style={{ width: `${percent}%` }}
									/>
								</div>
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
}

/** Rótulo curto do eixo X: a data ao final do nome ("Exam - 15/01/2026"). */
function shortLabel(name: string): string {
	return name.split(" - ").pop() ?? name;
}

function HistoryChart({ items }: { items: SubjectPerformanceChartItem[] }) {
	const width = 600;
	const height = 220;
	const padX = 40;
	const padTop = 28;
	const padBottom = 36;

	const [hovered, setHovered] = useState<number | null>(null);

	const points = items.map((item, index) => {
		const value = parsePercent(item.performance);
		const x =
			items.length === 1
				? width / 2
				: padX + (index * (width - padX * 2)) / (items.length - 1);
		const y =
			height - padBottom - (value / 100) * (height - padTop - padBottom);
		return { x, y, value, item };
	});

	const linePath = points
		.map((point, index) => `${index === 0 ? "M" : "L"}${point.x},${point.y}`)
		.join(" ");

	const activePoint = hovered !== null ? points[hovered] : null;
	// Geometria do tooltip (largura estimada pelo tamanho do texto).
	const tooltip = activePoint
		? (() => {
				const label = activePoint.item.name;
				const tw = Math.max(72, label.length * 6.2 + 20);
				const th = 24;
				const tx = Math.min(
					Math.max(activePoint.x - tw / 2, 4),
					width - tw - 4,
				);
				const above = activePoint.y - th - 14 >= 0;
				const ty = above ? activePoint.y - th - 14 : activePoint.y + 14;
				return { label, tw, th, tx, ty };
			})()
		: null;

	return (
		<svg
			viewBox={`0 0 ${width} ${height}`}
			className="w-full"
			role="img"
			aria-label="Histórico de desempenho na disciplina"
		>
			{/* Linhas de referência (0%, 50%, 100%) */}
			{[0, 50, 100].map((reference) => {
				const y =
					height -
					padBottom -
					(reference / 100) * (height - padTop - padBottom);
				return (
					<g key={reference}>
						<line
							x1={padX / 2}
							y1={y}
							x2={width - padX / 2}
							y2={y}
							className="stroke-slate-100"
							strokeWidth={1}
						/>
						<text
							x={padX / 2 - 4}
							y={y + 3}
							textAnchor="end"
							className="fill-slate-300 text-[10px]"
						>
							{reference}
						</text>
					</g>
				);
			})}

			{points.length > 1 && (
				<path
					d={linePath}
					fill="none"
					className="stroke-primary"
					strokeWidth={2}
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			)}

			{points.map((point, index) => (
				<g key={point.item.name}>
					<circle
						cx={point.x}
						cy={point.y}
						r={hovered === index ? 6 : 4}
						className="fill-primary transition-all"
					/>
					<text
						x={point.x}
						y={point.y - 10}
						textAnchor="middle"
						className="fill-slate-900 text-[11px] font-bold"
					>
						{point.value}%
					</text>
					<text
						x={point.x}
						y={height - 8}
						textAnchor="middle"
						className="fill-slate-400 text-[10px]"
					>
						{shortLabel(point.item.name)}
					</text>
					{/* Área de hover ampliada para exibir o nome da avaliação. */}
					{/* biome-ignore lint/a11y/useSemanticElements: ponto interativo dentro de <svg> não tem equivalente em <button> */}
					<circle
						cx={point.x}
						cy={point.y}
						r={16}
						fill="transparent"
						className="cursor-pointer"
						tabIndex={0}
						role="button"
						aria-label={point.item.name}
						onMouseEnter={() => setHovered(index)}
						onMouseLeave={() => setHovered(null)}
						onFocus={() => setHovered(index)}
						onBlur={() => setHovered(null)}
					/>
				</g>
			))}

			{/* Tooltip com o nome da avaliação no hover/foco do ponto. */}
			{tooltip && (
				<g pointerEvents="none">
					<rect
						x={tooltip.tx}
						y={tooltip.ty}
						width={tooltip.tw}
						height={tooltip.th}
						rx={6}
						className="fill-slate-900"
					/>
					<text
						x={tooltip.tx + tooltip.tw / 2}
						y={tooltip.ty + tooltip.th / 2 + 4}
						textAnchor="middle"
						className="fill-white text-[11px] font-medium"
					>
						{tooltip.label}
					</text>
				</g>
			)}
		</svg>
	);
}

function HistorySection({
	applicationId,
	subjectId,
}: {
	applicationId: string;
	subjectId: string;
}) {
	const { data, isLoading, error } = useSubjectPerformanceChart(
		applicationId,
		subjectId,
	);

	return (
		<div className="space-y-3">
			<h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
				<History size={16} className="text-primary" />
				Histórico de desempenho
			</h3>
			{isLoading ? (
				<Skeleton className="h-40 w-full rounded-xl" />
			) : error || !data ? (
				<div className="py-6 text-center text-sm text-slate-400 bg-slate-50 rounded-md">
					Não foi possível carregar o histórico de desempenho.
				</div>
			) : data.applicationsStudent.length === 0 ? (
				<div className="py-6 text-center text-sm text-slate-400 bg-slate-50 rounded-md">
					Nenhuma aplicação anterior desta disciplina no ano.
				</div>
			) : (
				<div className="p-4 bg-white border border-slate-100 rounded-xl">
					<HistoryChart items={data.applicationsStudent} />
				</div>
			)}
		</div>
	);
}

export function SubjectPerformanceModal({
	applicationId,
	subjectId,
	subjectName,
}: SubjectPerformanceModalProps) {
	const { data, isLoading, error } = useSubjectPerformance(
		applicationId,
		subjectId,
	);

	return (
		<DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
			<DialogHeader>
				<DialogTitle className="text-2xl font-bold uppercase tracking-wide text-slate-900">
					{data?.name ?? subjectName}
				</DialogTitle>
				<DialogDescription className="text-xs font-semibold uppercase tracking-wider text-slate-500">
					{data?.knowledgeArea ?? "Desempenho na disciplina"}
				</DialogDescription>
			</DialogHeader>

			{isLoading ? (
				<div className="space-y-4 pt-2">
					<div className="grid grid-cols-2 gap-4">
						<Skeleton className="h-24 w-full rounded-xl" />
						<Skeleton className="h-24 w-full rounded-xl" />
					</div>
					<Skeleton className="h-20 w-full rounded-xl" />
					<Skeleton className="h-32 w-full rounded-xl" />
				</div>
			) : error || !data ? (
				<div className="text-center py-10 text-slate-500">
					Não foi possível carregar o desempenho da disciplina.
				</div>
			) : (
				<div className="space-y-8 pt-2">
					{/* Nota e desempenho */}
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
						<div className="flex flex-col gap-1 p-5 bg-white border border-slate-100 rounded-xl shadow-sm">
							<span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
								Nota
							</span>
							<span className="text-3xl font-bold text-slate-900 tabular-nums">
								{data.score}
							</span>
						</div>
						<div className="flex flex-col gap-1 p-5 bg-white border border-slate-100 rounded-xl shadow-sm">
							<span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
								Desempenho
							</span>
							<span className="text-3xl font-bold text-slate-900 tabular-nums">
								{data.performance}
							</span>
						</div>
					</div>

					{/* Ranking (oculto quando desabilitado para a avaliação) */}
					{data.rank && (
						<div className="space-y-3">
							<h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
								<Trophy size={16} className="text-primary" />
								Ranking
							</h3>
							<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
								<RankCard icon={Users} label="Turma" rank={data.rank.class} />
								<RankCard
									icon={GraduationCap}
									label="Unidade"
									rank={data.rank.unity}
								/>
								<RankCard
									icon={Building2}
									label="Instituição"
									rank={data.rank.client}
								/>
							</div>
						</div>
					)}

					<CategorySection
						title="Desempenho por assunto"
						items={data.topics}
						emptyMessage="Nenhum assunto encontrado para esta disciplina."
					/>
					<CategorySection
						title="Desempenho por habilidade"
						items={data.abilities}
						emptyMessage="Nenhuma habilidade encontrada para esta disciplina."
					/>
					<CategorySection
						title="Desempenho por competência"
						items={data.competences}
						emptyMessage="Nenhuma competência encontrada para esta disciplina."
					/>

					<HistorySection applicationId={applicationId} subjectId={subjectId} />
				</div>
			)}
		</DialogContent>
	);
}
